import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { StatCategory } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { findThresholdColor } from '@sorare/core/src/lib/thresholds';

import { Category } from '../Category';
import { DecisiveScoreGauge } from '../DecisiveScoreGauge';
import { GAME_SCORE_TO_SPORT, isCategoryDecisive } from '../utils';
import type { DecisiveScore_playerGameScoreInterface } from './__generated__/index.graphql';

const Details = styled(Vertical)`
  > * {
    overflow: hidden;
  }
  > *:first-child {
    border-top-left-radius: var(--double-unit);
    border-top-right-radius: var(--double-unit);
  }
  > *:last-child {
    border-bottom-left-radius: var(--double-unit);
    border-bottom-right-radius: var(--double-unit);
  }
  > *:not(:first-child) {
    border-top: 1px solid var(--c-nd-200);
  }
`;
const GaugeWrapper = styled.div`
  padding: var(--double-unit);
  background: var(--c-nd-100-opaque);
`;

type Props = {
  playerGameScore: DecisiveScore_playerGameScoreInterface;
};

export const DecisiveScore = ({ playerGameScore }: Props) => {
  const { formatNumber } = useIntlContext();
  const decisiveScore =
    isType(playerGameScore, 'PlayerGameScore') ||
    isType(playerGameScore, 'So5Score')
      ? playerGameScore.decisiveScore?.totalScore
      : null;

  const decisiveStats = playerGameScore.detailedScore.filter(stat =>
    isCategoryDecisive(stat.category)
  );

  const groupedStats = groupBy(decisiveStats, stat => stat.category);
  const { color } = findThresholdColor(
    decisiveScore ?? 0,
    GAME_SCORE_TO_SPORT[playerGameScore.__typename],
    'CARD_SCORE'
  );
  return (
    <Vertical>
      <Horizontal>
        <BodyM bold>
          <FormattedMessage {...fantasy.decisiveScore} />
        </BodyM>
        <BodyM as="p" bold color={color}>
          <FormattedMessage
            {...fantasy.pointsWithStyledScoreWithoutFormat}
            values={{
              score: formatNumber(Number(decisiveScore?.toFixed(2))),
              span: chunks => <span>{chunks}</span>,
            }}
          />
        </BodyM>
      </Horizontal>
      <Details gap={0}>
        <GaugeWrapper>
          <DecisiveScoreGauge
            score={decisiveScore ?? 0}
            sport={GAME_SCORE_TO_SPORT[playerGameScore.__typename]}
          />
        </GaugeWrapper>
        {Object.keys(groupedStats).map(category => (
          <Category
            key={category}
            category={category as StatCategory}
            playerGameScores={[playerGameScore]}
            stats={groupedStats[category as StatCategory]!}
          />
        ))}
      </Details>
    </Vertical>
  );
};

DecisiveScore.fragments = {
  playerGameScoreInterface: gql`
    fragment DecisiveScore_playerGameScoreInterface on PlayerGameScoreInterface {
      id
      anyGame {
        id
        statusTyped
      }
      detailedScore {
        stat
        statValue
        category
        ...Category_statScore
      }
      ... on PlayerGameScore {
        id
        decisiveScore {
          totalScore
        }
      }
      ... on So5Score {
        id
        decisiveScore {
          totalScore
        }
      }
      ...Category_playerGameScoreInterface
    }
    ${Category.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<DecisiveScore_playerGameScoreInterface>,
};
